/**
 * Pure core of the in-app error reporting flow: normalize whatever the runtime
 * threw, decide whether it's worth surfacing to the user, and shape it into the
 * `/api/feedback` ticket payload. Everything DOM/network lives in thin wrappers
 * (captureScreenshot.ts, the modal components); this module takes plain values
 * so the whole flow is unit-testable.
 */

export type ErrorSource = 'render' | 'window' | 'promise'

export interface NormalizedError {
  message: string
  /** Error/DOMException name (e.g. "AbortError") — a DOMException's message
   * never contains it, so filtering on message alone misses aborts. */
  name?: string
  stack?: string
  /** React component stack, only for errors caught by the ErrorBoundary. */
  componentStack?: string
  source: ErrorSource
}

const UNKNOWN_MESSAGE = 'Unknown error'

export function normalizeCaughtValue(value: unknown, source: ErrorSource): NormalizedError {
  if (value instanceof Error) {
    return {
      message: value.message || UNKNOWN_MESSAGE,
      name: value.name || undefined,
      stack: value.stack,
      source,
    }
  }
  if (typeof value === 'string') {
    return { message: value || UNKNOWN_MESSAGE, source }
  }
  if (value && typeof value === 'object' && 'message' in value) {
    const obj = value as { message: unknown; name?: unknown; stack?: unknown }
    return {
      message: String(obj.message) || UNKNOWN_MESSAGE,
      name: typeof obj.name === 'string' && obj.name ? obj.name : undefined,
      stack: typeof obj.stack === 'string' ? obj.stack : undefined,
      source,
    }
  }
  if (value === null || value === undefined) {
    return { message: UNKNOWN_MESSAGE, source }
  }
  return { message: String(value), source }
}

// Noise we never want to nag a family member about: cross-origin scripts we
// can't see into, browser-extension breakage, benign ResizeObserver warnings,
// Next.js control-flow "errors", cancelled requests, and plain network blips
// (the offline layer already gives those an honest in-app UI).
const IGNORED_MESSAGES: RegExp[] = [
  /^Script error\.?$/,
  /ResizeObserver loop/,
  /^NEXT_REDIRECT/,
  /^NEXT_NOT_FOUND/,
  /operation was aborted/i,
  /signal is aborted/i,
  /^AbortError/,
  /^Failed to fetch/,
  /^NetworkError/,
  /^Load failed$/,
]

const EXTENSION_URL = /(chrome|moz|safari-web)-extension:\/\//

export function shouldReportError(input: {
  message: string
  source: ErrorSource
  name?: string
  stack?: string
  filename?: string
}): boolean {
  const message = input.message.trim()
  if (!message || message === UNKNOWN_MESSAGE) return false
  // Aborts are cancellations, not failures. Chromium puts "AbortError" only in
  // .name and the stack's first frame — never in .message — so the message
  // regexes alone let real aborts through.
  if (input.name === 'AbortError') return false
  if (input.stack?.startsWith('AbortError')) return false
  if (IGNORED_MESSAGES.some((re) => re.test(message))) return false
  if (input.filename && EXTENSION_URL.test(input.filename)) return false
  if (input.stack && EXTENSION_URL.test(input.stack)) return false
  return true
}

export interface ErrorDiagnostics {
  message: string
  stack?: string
  componentStack?: string
  source: ErrorSource
  buildId?: string
  capturedAt?: string
}

export interface ErrorTicket {
  type: 'bug'
  appId: string | null
  subject: string
  description: string
  url?: string
  diagnostics: ErrorDiagnostics
}

const SUBJECT_MAX = 80

export function buildErrorTicket(input: {
  report: NormalizedError
  userNote?: string
  url?: string
  appId?: string | null
  buildId?: string
  timestamp?: string
}): ErrorTicket {
  const { report } = input
  const firstLine = report.message.split('\n')[0].trim()
  const subject = `Error: ${firstLine.length > SUBJECT_MAX ? firstLine.slice(0, SUBJECT_MAX) + '…' : firstLine}`

  const note = input.userNote?.trim()
  const autoLine = `Automatically captured error report:\n${report.message}`
  const description = note ? `${note}\n\n${autoLine}` : autoLine

  const diagnostics: ErrorDiagnostics = {
    message: report.message,
    source: report.source,
  }
  if (report.stack) diagnostics.stack = report.stack
  if (report.componentStack) diagnostics.componentStack = report.componentStack
  if (input.buildId) diagnostics.buildId = input.buildId
  if (input.timestamp) diagnostics.capturedAt = input.timestamp

  return {
    type: 'bug',
    appId: input.appId ?? null,
    subject,
    description,
    url: input.url,
    diagnostics,
  }
}

export function buildClipboardText(input: {
  report: NormalizedError
  url?: string
  timestamp?: string
}): string {
  const { report } = input
  const lines = [
    `Error: ${report.message}`,
    '',
    `URL: ${input.url ?? 'N/A'}`,
    `Timestamp: ${input.timestamp ?? 'N/A'}`,
    '',
    'Stack trace:',
    report.stack ?? 'N/A',
  ]
  if (report.componentStack) {
    lines.push('', 'Component stack:', report.componentStack)
  }
  return lines.join('\n')
}

// Server-side guard: the diagnostics column is written from a client payload,
// so only allowlisted string fields survive, each capped so one giant stack
// can't bloat the row.
const DIAGNOSTIC_KEYS = ['message', 'stack', 'componentStack', 'source', 'buildId', 'capturedAt'] as const
const DIAGNOSTIC_VALUE_MAX = 8000

export function sanitizeDiagnostics(value: unknown): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const source = value as Record<string, unknown>
  const out: Record<string, string> = {}
  for (const key of DIAGNOSTIC_KEYS) {
    const v = source[key]
    if (typeof v === 'string' && v) {
      out[key] = v.length > DIAGNOSTIC_VALUE_MAX ? v.slice(0, DIAGNOSTIC_VALUE_MAX) : v
    }
  }
  return Object.keys(out).length > 0 ? out : null
}

/** Object path inside the feedback-screenshots bucket; the first segment is the
 * owner's user id, which is what the storage RLS gates on. */
export function screenshotStoragePath(userId: string, fileId: string): string {
  return `${userId}/${fileId}.jpg`
}

/** Scale factor that brings a capture's long edge down to `maxEdge` px. */
export function screenshotJpegScale(width: number, height: number, maxEdge: number): number {
  const longEdge = Math.max(width, height)
  return longEdge > maxEdge ? maxEdge / longEdge : 1
}

/** Pull the app id out of an `/apps/<id>/...` pathname. */
export function appIdFromPath(pathname: string | null): string | null {
  if (!pathname) return null
  const match = pathname.match(/^\/apps\/([^/]+)/)
  return match ? match[1] : null
}
